import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { imageSrc, makeMediaErrorRetry } from '../api/media';
import { getAppSettings } from '../api/settings';
import { deleteStory, markStoryViewed, setStoryAuthorMuted, type StoryAuthorDto } from '../api/stories';
import Avatar from './Avatar';

/** Индекс первой непросмотренной сторис автора — viewed берём из переданного набора (см.
 *  viewedIds ниже: начальное состояние — из feed-проп с сервера, затем ведётся локально в
 *  рамках сессии просмотра, чтобы не ждать round-trip на каждый переход между сторис). */
function firstUnviewedIndex(author: StoryAuthorDto | undefined, viewed: Set<string>): number {
  if (!author) return 0;
  const idx = author.stories.findIndex((s) => !viewed.has(s.id));
  return idx >= 0 ? idx : 0;
}

/** Просмотрщик сторис: полный экран, медиа по центру, полосы прогресса по каждой сторис автора
 *  (текущая заполняется), автопереход с длительностью из настроек приложения (фото 5 с, видео
 *  15 с по умолчанию — настраивается в админ-панели, см. AdminPanel «Сторис»), зоны листания
 *  влево/вправо (тап по трети экрана, свайп или кнопки), ✕/фон — закрыть. "⋮" в шапке —
 *  удалить свою сторис или скрыть сторисы чужого автора (см. lib/mutedStoryAuthors).
 *
 *  Просмотренные сторис: помечаются при показе, при автопереходе пропускаются (не
 *  воспроизводятся заново), при открытии автора начинаем с первой непросмотренной; когда
 *  непросмотренных не осталось ни у кого — просмотр закрывается (см. отчёт пользователя). */
export default function StoryViewer({ feed, start, onClose, onDeleted, onMuted }: {
  feed: StoryAuthorDto[];
  start: number;
  onClose: () => void;
  onDeleted: () => void;
  /** Автора замьютили ("Не показывать сторис [имя]", см. story-topbar-menu) — обновить ленту
   *  сторис в ChatList (тот же сигнал, что и onDeleted, но не удаление, а фильтр по авторам). */
  onMuted: () => void;
}) {
  const { t } = useTranslation('stories');
  // Снимок просмотренности с сервера (feed проп) на момент открытия + локальные отметки за
  // время текущей сессии просмотра (см. markStoryViewed-эффект ниже) — без этого next()
  // пришлось бы либо ждать API-ответ перед каждым переходом, либо заново запрашивать feed.
  const [viewedIds, setViewedIds] = useState<Set<string>>(
    () => new Set(feed.flatMap((a) => a.stories.filter((s) => s.viewed).map((s) => s.id))),
  );
  const [authorIdx, setAuthorIdx] = useState(Math.max(0, Math.min(start, feed.length - 1)));
  const [storyIdx, setStoryIdx] = useState<number>(
    () => firstUnviewedIndex(feed[Math.max(0, Math.min(start, feed.length - 1))], viewedIds),
  );
  const [src, setSrc] = useState<string | null>(null);
  const [delMsg, setDelMsg] = useState('');
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  const timerRef = useRef<number | null>(null);
  // Длительность показа (сек) — из настроек приложения, по умолчанию 5/15 (см. AdminPanel).
  const [photoSeconds, setPhotoSeconds] = useState(5);
  const [videoSeconds, setVideoSeconds] = useState(15);

  const author = feed[authorIdx];
  const story = author?.stories[storyIdx];
  const isVideo = !!story?.mimeType?.startsWith('video');
  // Аудио-сторис (см. отчёт пользователя "дать возможность разместить аудио") — как видео:
  // авто-переход по onEnded, а не по фиксированному таймеру фото (аудио обычно длиннее 5с).
  const isAudio = !!story?.mimeType?.startsWith('audio');
  const currentSeconds = isVideo || isAudio ? videoSeconds : photoSeconds;

  useEffect(() => {
    let alive = true;
    getAppSettings().then((s) => {
      if (!alive) return;
      setPhotoSeconds(s.storyPhotoSeconds || 5);
      setVideoSeconds(s.storyVideoSeconds || 15);
    }).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setSrc(null);
    setTopbarMenuOpen(false);
    if (!story) return;
    let alive = true;
    imageSrc(story.mediaFileId).then((u) => alive && setSrc(u)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [authorIdx, storyIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Показ сторис = просмотрена (для ленты/полосок) — локально сразу (see viewedIds выше,
  // next() читает его синхронно), на сервер — фоном, не блокируя переход.
  useEffect(() => {
    if (!story?.id) return;
    setViewedIds((prev) => (prev.has(story.id) ? prev : new Set(prev).add(story.id)));
    markStoryViewed(story.id).catch(() => {});
  }, [story?.id]);

  // Автопереход: фото — photoSeconds, видео — videoSeconds (см. отчёт пользователя
  // "длительность просмотра сторисов... 5 секунд фото, 15 секунд видео"). Для видео onEnded
  // ниже листает раньше, если ролик короче заданной длительности.
  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(next, currentSeconds * 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorIdx, storyIdx, currentSeconds]);

  const next = () => {
    if (!author) return;
    // Следующая непросмотренная этого автора (просмотренные не переигрываем — см. отчёт
    // пользователя "просмотренные ранее сторис не воспроизводить заново").
    let idx = storyIdx + 1;
    while (idx < author.stories.length && viewedIds.has(author.stories[idx].id)) idx++;
    if (idx < author.stories.length) {
      setStoryIdx(idx);
      return;
    }
    // Следующий автор, у которого есть непросмотренные.
    for (let a = authorIdx + 1; a < feed.length; a++) {
      const first = feed[a].stories.findIndex((s) => !viewedIds.has(s.id));
      if (first >= 0) {
        setAuthorIdx(a);
        setStoryIdx(first);
        return;
      }
    }
    // Всё просмотрено — закрываем просмотр (см. отчёт пользователя "при окончании просмотра
    // сторис — закрывать просмотр").
    onClose();
  };
  const prev = () => {
    if (storyIdx > 0) {
      setStoryIdx(storyIdx - 1);
    } else if (authorIdx > 0) {
      setAuthorIdx(authorIdx - 1);
      setStoryIdx((feed[authorIdx - 1]?.stories.length ?? 1) - 1);
    }
  };

  // Свайп/тап по самой сторис — как в Telegram/Instagram (см. отчёт пользователя "добавить
  // возможность пролистывать сторисы пальцем или по кнопкам"): горизонтальный свайп дальше
  // порога — пред./след., иначе обычный тап делится на левую треть (пред.) и остальное
  // (след.); .story-nav-*/шапка/крестик — свои кнопки, тут их клики не перехватываем.
  // video/audio — тоже исключены: у видео-сторис свои нативные controls (play/pause/перемотка,
  // см. <video controls>), и любой клик/драг по ним (в т.ч. перетаскивание ползунка перемотки)
  // иначе читался бы как тап/свайп и уводил на след./закрывал просмотр прямо во время
  // взаимодействия с плеером — сторис выглядела как "не воспроизводится" (см. отчёт
  // пользователя: клик по play на видео сразу закрывал/перелистывал просмотр).
  const SWIPE_PX = 50;
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const onContentPointerDown = (e: React.PointerEvent) => {
    dragStartRef.current = null;
    if ((e.target as HTMLElement).closest('button, a, video, audio, .menu')) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };
  const onContentPointerUp = (e: React.PointerEvent) => {
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (!start) return;
    if ((e.target as HTMLElement).closest('button, a, video, audio, .menu')) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (dy > SWIPE_PX && dy > Math.abs(dx)) {
      onClose();
      return;
    }
    if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) prev();
      else next();
      return;
    }
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      if (ratio < 0.3) prev();
      else next();
    }
  };

  const keyHandlerRef = useRef<(event: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'Escape') onClose();
    // Стрелки внутри плеера оставляем его штатной перемотке.
    else if (!(event.target as HTMLElement | null)?.closest?.('video, audio, input, textarea')) {
      if (event.key === 'ArrowRight') { event.preventDefault(); next(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); prev(); }
    }
  };
  useEffect(() => {
    const handler = (event: KeyboardEvent) => keyHandlerRef.current(event);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (!author || !story) return null;

  return (
    <div className="story-viewer" onClick={onClose}>
      <div
        className="story-viewer-content"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onContentPointerDown}
        onPointerUp={onContentPointerUp}
        onPointerCancel={() => { dragStartRef.current = null; }}
      >
        {/* Полосы прогресса: по одной на каждую сторис автора; готовые — заполнены, текущая
            анимированно заполняется за time (сброс через key=storyIdx — React пересоздаёт
            элемент, анимация стартует заново). */}
        <div className="story-progress-row">
          {author.stories.map((s, i) => (
            <div key={s.id} className={'story-progress-seg' + (i < storyIdx ? ' done' : '')}>
              {i === storyIdx && (
                <div
                  key={storyIdx}
                  className="story-progress-fill"
                  style={{ animationDuration: `${currentSeconds}s` }}
                />
              )}
            </div>
          ))}
        </div>
        <div className="story-topbar">
          <Avatar userId={author.user.id} name={author.user.displayName} size={30} />
          <div className="story-author-name">{author.user.displayName}</div>
          <div className="story-topbar-menu-wrap">
            <button
              type="button"
              className="story-topbar-menu-btn"
              title={t('more')}
              onClick={() => setTopbarMenuOpen((v) => !v)}
            >
              ⋮
            </button>
            {topbarMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setTopbarMenuOpen(false)} />
                <div className="menu story-topbar-menu">
                  {story.isMine ? (
                    <button
                      type="button"
                      className="menu-item danger"
                      onClick={async () => {
                        setTopbarMenuOpen(false);
                        try {
                          await deleteStory(story.id);
                          setDelMsg(t('deleted'));
                          onDeleted();
                          onClose();
                        } catch {
                          setDelMsg(t('deleteFailed'));
                        }
                      }}
                    >
                      {t('delete')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="menu-item"
                      onClick={async () => {
                        setTopbarMenuOpen(false);
                        try {
                          await setStoryAuthorMuted(author.user.id, true);
                        } catch {
                          /* не критично — просто останется видимым в этот раз */
                        }
                        onMuted();
                        onClose();
                      }}
                    >
                      {t('muteAuthor', { name: author.user.displayName })}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {delMsg && <div style={{ color: 'var(--muted)', fontSize: 13 }}>{delMsg}</div>}
        <div className="story-media">
          {src && (isVideo ? (
            <video src={src} className="story-video" autoPlay controls playsInline onEnded={next} onError={makeMediaErrorRetry(story.mediaFileId, src, setSrc)} />
          ) : isAudio ? (
            <div className="story-audio-wrap">
              <div className="story-audio-icon">🎵</div>
              <audio src={src} className="story-audio" autoPlay controls onEnded={next} onError={makeMediaErrorRetry(story.mediaFileId, src, setSrc)} />
            </div>
          ) : (
            <img src={src} alt="" className="story-img" />
          ))}
        </div>
        {story.caption && <div className="story-caption">{story.caption}</div>}
        <button type="button" className="story-nav story-nav-left" onClick={prev} aria-label={t('previous')}>‹</button>
        <button type="button" className="story-nav story-nav-right" onClick={next} aria-label={t('next')}>›</button>
      </div>
      <button type="button" className="story-close" title={t('close')} onClick={onClose}>✕</button>
    </div>
  );
}
